/* eslint-disable indent */
import { FunctionComponent, ReactElement, RefObject, useEffect, useMemo, useState } from 'react';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import { AdvisorPageLayout } from '@/components/layouts/advisor-page';
import { PortfoliosTable } from '@/shared/components/portfolios-table';
import FileInput from '@/shared/components/file-input';
import { FileStatus, useFileUpload } from '@/shared/hooks/use-file-upload';
import { useAllPortfolios, useUploadTemplatePortfolios } from './manage-portfolios.queries';
import { DEFAULT_PAGE } from '@/clients/clients.constants';
import { FileInputRef } from '@/shared/components/file-input/FileInput';

const ManagePortfoliosPage: FunctionComponent = (): ReactElement => {
	const [page, setPage] = useState<number>(DEFAULT_PAGE);
	const title = useMemo(() => 'Manage Portfolios', []);
	const { fileInputRef, file, status, onFileChange, setFile, setStatus, onFileRemoved } =
		useFileUpload();

	const { data } = useAllPortfolios({ page });
	const { mutate, isPending: isUpdating } = useUploadTemplatePortfolios();

	const resetFileState = () => {
		setFile(undefined);
		setStatus(undefined);
	};

	useEffect(() => {
		if (!file) return;

		const formData = new FormData();
		formData.append('file', file);

		mutate(formData, {
			onSuccess: () => {
				resetFileState();
			},
			onError: () => {
				onFileRemoved();
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [file]);

	return (
		<AdvisorPageLayout title={title}>
			<Box component={Paper} padding="1.5rem">
				<Stack direction="row" alignItems="center" justifyContent="space-between" gap="1rem">
					<Typography component="h2" variant="h5">
						Manage Portfolios
					</Typography>

					<FileInput
						id="upload-csv"
						name="upload-csv"
						buttonText="Upload CSV"
						accept=".csv"
						onChange={onFileChange}
						ref={fileInputRef as RefObject<FileInputRef>}
						disabled={(file && status !== FileStatus.LOADED) || isUpdating}
					/>
				</Stack>

				<Divider
					sx={{
						marginTop: '1.5rem',
						marginBottom: '2rem',
					}}
				/>

				<PortfoliosTable data={data} onPageChange={(page) => setPage(page)} />
			</Box>
		</AdvisorPageLayout>
	);
};

export default ManagePortfoliosPage;
